import {
    Accordion,
    AccordionDetails,
    AccordionSummary,
    Alert,
    Box,
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Grid,
    TextField,
    Typography,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import { makeStyles } from '@mui/styles';
import React from 'react';

import { DataAPIContext } from '../api';
import { UPDATE } from '../api/dataProvider';
import { RemoteAutocomplete } from '../components/RemoteAutocomplete';
import SandboxedHtmlPreview from '../components/SandboxedHtmlPreview';

const useStyles = makeStyles((theme: any) => ({
    resultPane: {
        background: '#f7f7f7',
        border: '1px solid #ddd',
        padding: theme.spacing(2),
        maxHeight: 320,
        overflow: 'auto',
        fontFamily: 'monospace',
        fontSize: 13,
        whiteSpace: 'pre-wrap',
        wordBreak: 'break-word',
    },
    // Subject and other "inbox-style" fields render in a natural proportional
    // font so the preview reads like what a recipient would actually see.
    naturalPane: {
        background: 'white',
        border: '1px solid #ddd',
        padding: theme.spacing(1.5, 2),
        fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
        fontSize: 15,
        fontWeight: 600,
        color: '#1a1a1a',
        wordBreak: 'break-word',
    },
    fieldLabel: {
        fontWeight: 600,
        marginBottom: theme.spacing(0.5),
    },
}));

const FALLBACK_EVENT_CODE = 'tour_participant.created';
const DEFAULT_SOURCE_DATA = '{"gtu": 0}';

// Field names that should be rendered as HTML preview rather than monospaced text.
const HTML_FIELDS = new Set(['email_message']);

// Field names that should be displayed in a natural proportional font (mimics
// how a recipient would see the field in their email client, not a code view).
const NATURAL_FIELDS = new Set(['email_subject']);

interface Props {
    open: boolean;
    onClose: () => void;
    notificationId: number;
    deliveryMethodId: number;
    methodLabel: string;  // e.g. "Email", "SMS"
    defaultEventCode?: string;  // pre-fill from parent notification.event
    // When provided, the dialog reads in-flight (unsaved) field values from
    // this callback at render time and sends them as field_overrides. The
    // callback is invoked synchronously when "Render" is clicked, so it
    // always sees the latest CodeMirror value rather than a stale snapshot.
    getFieldOverrides?: () => Record<string, string | undefined | null>;
    hasUnsavedChanges?: boolean;
}

interface PreviewResponse {
    rendered: Record<string, string>;
    method: number;
    context_resolved: boolean;
    uses_standard_template?: boolean;
}

interface PreviewSource {
    id: string;              // == delivery_log_id (UUID string, RemoteAutocomplete keys by .id)
    delivery_log_id: string;
    gtu_id: number;
    gtu_label: string;
    created_ts: string | null;
}

const DeliveryMethodPreviewDialog: React.FC<Props> = ({ open, onClose, notificationId, deliveryMethodId, methodLabel, defaultEventCode, getFieldOverrides, hasUnsavedChanges }) => {
    const classes = useStyles();
    const dataAPI = React.useContext(DataAPIContext) as any;

    const initialEventCode = defaultEventCode || FALLBACK_EVENT_CODE;
    const [eventCode, setEventCode] = React.useState(initialEventCode);
    // The Event code field is free text, and the recent-deliveries lookup it
    // keys walks daily delivery-log partitions on the backend — so the lookup
    // sees a debounced copy, not one query per keystroke.
    const [lookupEventCode, setLookupEventCode] = React.useState(initialEventCode);
    React.useEffect(() => {
        if (lookupEventCode === eventCode) return undefined;
        const tm = setTimeout(() => setLookupEventCode(eventCode), 600);
        return () => clearTimeout(tm);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [eventCode]);
    // Primary picker: a real prior delivery (full primitives via EDA).
    const [selectedSource, setSelectedSource] = React.useState<PreviewSource | null>(null);
    // Fallback picker: any participant — backend synthesizes the rest of the primitives.
    const [sampleGtu, setSampleGtu] = React.useState<any>(null);
    const [sourceDataText, setSourceDataText] = React.useState(DEFAULT_SOURCE_DATA);
    const [advancedOpen, setAdvancedOpen] = React.useState(false);
    const [loading, setLoading] = React.useState(false);
    const [result, setResult] = React.useState<PreviewResponse | null>(null);
    const [error, setError] = React.useState<string | null>(null);
    // Hide raw HTML source by default — the rendered preview is what most ops
    // want, and hiding the raw text gives the preview more vertical space.
    const [showRawHtmlSource, setShowRawHtmlSource] = React.useState(false);

    React.useEffect(() => {
        if (open) {
            setResult(null);
            setError(null);
            setSelectedSource(null);
            setSampleGtu(null);
            setAdvancedOpen(false);
            // Re-sync the event code in case the parent notification's event changes
            // while the dialog stays mounted.
            setEventCode(defaultEventCode || FALLBACK_EVENT_CODE);
        }
    }, [open, defaultEventCode]);

    // Drop both picks when the event code changes — the recent-deliveries
    // dropdown is scoped to event_code, and the synthesized primitives are
    // event-class-specific.
    React.useEffect(() => {
        setSelectedSource(null);
        setSampleGtu(null);
    }, [eventCode]);

    const eventCodeAcceptsGtu = eventCode.startsWith('tour_participant.');

    const onRender = async () => {
        setError(null);
        setResult(null);

        // Preference order matches the backend:
        //   1. real prior delivery (delivery_log_id) — most accurate
        //   2. synthesized event around a chosen participant (sample_gtu_id)
        //   3. hand-edited primitives (advanced override)
        const body: Record<string, any> = {
            dm_id: deliveryMethodId,
            event_code: eventCode,
        };

        // Pick up in-flight field values (subject + CodeMirror body) so the
        // preview reflects unsaved edits. Empty/null values are dropped so the
        // backend's "use saved DM value" path still wins for fields the parent
        // doesn't actively edit.
        if (getFieldOverrides) {
            const raw = getFieldOverrides() || {};
            const filtered: Record<string, string> = {};
            for (const [k, v] of Object.entries(raw)) {
                if (typeof v === 'string' && v.length > 0) {
                    filtered[k] = v;
                }
            }
            if (Object.keys(filtered).length > 0) {
                body.field_overrides = filtered;
            }
        }

        if (selectedSource) {
            body.delivery_log_id = selectedSource.delivery_log_id;
        } else if (sampleGtu?.id != null) {
            body.sample_gtu_id = sampleGtu.id;
        } else if (advancedOpen) {
            let sourceData;
            try {
                sourceData = JSON.parse(sourceDataText);
            } catch (e: any) {
                setError(`Source data is not valid JSON: ${e.message}`);
                return;
            }
            if (typeof sourceData !== 'object' || sourceData === null || Array.isArray(sourceData)) {
                setError('Source data must be a JSON object');
                return;
            }
            body.source_data = sourceData;
        } else {
            setError('Pick a recent delivery or a sample participant to preview against.');
            return;
        }

        setLoading(true);
        try {
            const res = await dataAPI(UPDATE, 'notification', {
                id: notificationId,
                urlSuffix: 'preview_delivery_method/',
                data: body,
            });
            if (res.statusCode === 200) {
                setResult(res.data);
            } else {
                setError(res.data?.message || `Render failed (status ${res.statusCode})`);
            }
        } catch (e: any) {
            setError(`Request failed: ${e.message || e}`);
        } finally {
            setLoading(false);
        }
    };

    const canRender =
        !!eventCode &&
        (selectedSource != null || sampleGtu != null || (advancedOpen && !!sourceDataText));

    const renderedFields = result?.rendered ? Object.entries(result.rendered) : [];

    return (
        <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
            <DialogTitle>Test Render — {methodLabel}</DialogTitle>
            <DialogContent>
                <Grid container spacing={2}>
                    <Grid item xs={12}>
                        <Typography variant="body2" color="textSecondary">
                            Render this delivery method's templates against a sample event source. Nothing is saved or sent.
                        </Typography>
                    </Grid>

                    {hasUnsavedChanges && (
                        <Grid item xs={12}>
                            <Alert severity="info">
                                Preview will use your unsaved edits to the subject and message body.
                            </Alert>
                        </Grid>
                    )}

                    <Grid item xs={12}>
                        <TextField
                            label="Event code"
                            variant="filled"
                            size="small"
                            fullWidth
                            value={eventCode}
                            onChange={(e) => setEventCode(e.target.value)}
                            helperText="e.g. tour_participant.created"
                        />
                    </Grid>

                    <Grid item xs={12}>
                        <Typography variant="caption" color="textSecondary">
                            Pick something to preview against. The first option uses a real prior delivery (most accurate); the second builds a sample around any participant if nothing has fired yet.
                        </Typography>
                    </Grid>

                    <Grid item xs={12}>
                        <RemoteAutocomplete
                            resource="notification"
                            urlSuffix="preview_sources/"
                            apiParams={{ event_code: lookupEventCode }}
                            refreshKey={lookupEventCode}
                            deferFetchUntilOpen
                            label="Recent delivery (preferred)"
                            variant="filled"
                            size="small"
                            fullWidth
                            optionsResultsSize={20}
                            value={selectedSource}
                            getOptionLabel={(opt: any) => opt?.gtu_label || ''}
                            onChange={(_ev: any, source: any) => {
                                setSelectedSource(source || null);
                                if (source) {
                                    // Picking a real delivery supersedes a sample pick.
                                    setSampleGtu(null);
                                }
                            }}
                            noOptionsText={`No recent deliveries of ${eventCode}`}
                        />
                    </Grid>

                    {eventCodeAcceptsGtu && (
                        <Grid item xs={12}>
                            <RemoteAutocomplete
                                resource="notification"
                                urlSuffix="preview_participants/"
                                label="Or pick any participant (sample preview)"
                                variant="filled"
                                size="small"
                                fullWidth
                                optionsResultsSize={20}
                                value={sampleGtu}
                                getOptionLabel={(opt: any) => {
                                    if (!opt || !opt.user) return '';
                                    const name = opt.user.display_name || opt.user.email || '(no name)';
                                    const tour = opt.group_tour?.name;
                                    return tour ? `${name} — ${tour}` : name;
                                }}
                                onChange={(_ev: any, gtu: any) => {
                                    setSampleGtu(gtu || null);
                                    if (gtu) {
                                        // Picking a sample participant supersedes a real-delivery pick.
                                        setSelectedSource(null);
                                    }
                                }}
                            />
                            {sampleGtu && (
                                <Alert severity="info" sx={{ marginTop: 1 }}>
                                    Sample mode: non-essential fields use placeholder values like
                                    {' '}<code>&lt;sample message&gt;</code>. The participant, tour,
                                    and operator-specific context are real.
                                </Alert>
                            )}
                        </Grid>
                    )}

                    <Grid item xs={12}>
                        <Accordion
                            expanded={advancedOpen}
                            onChange={(_, isOpen) => setAdvancedOpen(isOpen)}
                            elevation={0}
                            disableGutters
                            sx={{ '&:before': { display: 'none' }, background: 'transparent' }}
                        >
                            <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={{ paddingLeft: 0 }}>
                                <Typography variant="caption" color="textSecondary">
                                    Advanced: raw source data (JSON)
                                </Typography>
                            </AccordionSummary>
                            <AccordionDetails sx={{ paddingX: 0 }}>
                                <TextField
                                    label="Source data (JSON)"
                                    variant="filled"
                                    size="small"
                                    fullWidth
                                    multiline
                                    minRows={3}
                                    maxRows={8}
                                    value={sourceDataText}
                                    onChange={(e) => setSourceDataText(e.target.value)}
                                    helperText="Used only when neither picker above is set. Must contain the full primitives the event class expects."
                                />
                            </AccordionDetails>
                        </Accordion>
                    </Grid>

                    {error && (
                        <Grid item xs={12}>
                            <Alert severity="error">{error}</Alert>
                        </Grid>
                    )}

                    {result?.context_resolved === false && (
                        <Grid item xs={12}>
                            <Alert severity="warning">
                                Some context fields could not be fully resolved (lazy-evaluated fields).
                                These would resolve at real fire time but show as placeholders here.
                            </Alert>
                        </Grid>
                    )}

                    {result?.uses_standard_template && (
                        <Grid item xs={12}>
                            <Alert severity="info">
                                This delivery method renders a Standard Template (Template type = Standard).
                                Edits to the custom email body don't take effect in production unless you
                                switch Template type to Custom.
                            </Alert>
                        </Grid>
                    )}

                    {renderedFields.map(([fieldName, value]) => (
                        <Grid key={fieldName} item xs={12}>
                            <Typography variant="subtitle2" className={classes.fieldLabel}>
                                {fieldName}
                            </Typography>
                            {value ? (
                                NATURAL_FIELDS.has(fieldName) ? (
                                    <Box className={classes.naturalPane}>{value}</Box>
                                ) : HTML_FIELDS.has(fieldName) ? (
                                    <>
                                        <SandboxedHtmlPreview
                                            html={value}
                                            title={`Rendered ${fieldName} preview`}
                                            // Give the rendered preview more vertical room when
                                            // the raw source is hidden (the common case).
                                            style={{ height: showRawHtmlSource ? 320 : 600 }}
                                        />
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, marginTop: 1 }}>
                                            <Button
                                                size="small"
                                                variant="text"
                                                onClick={() => setShowRawHtmlSource((v) => !v)}
                                                sx={{ textTransform: 'none', padding: '2px 6px', minWidth: 0 }}
                                            >
                                                {showRawHtmlSource ? 'Hide raw source ▴' : 'Show raw source ▾'}
                                            </Button>
                                        </Box>
                                        {showRawHtmlSource && (
                                            <Box className={classes.resultPane} sx={{ marginTop: 1 }}>{value}</Box>
                                        )}
                                    </>
                                ) : (
                                    <Box className={classes.resultPane}>{value}</Box>
                                )
                            ) : (
                                <Typography variant="caption" color="textSecondary">
                                    (empty)
                                </Typography>
                            )}
                        </Grid>
                    ))}
                </Grid>
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose}>Close</Button>
                <Button
                    variant="contained"
                    color="primary"
                    startIcon={<PlayArrowIcon />}
                    onClick={onRender}
                    disabled={loading || !canRender}
                >
                    {loading ? 'Rendering…' : 'Render'}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default DeliveryMethodPreviewDialog;
